import type { ChatAttachmentView } from '@/components/ChatMessageAttachments';
import type { ChatMessageView } from '@/components/ChatMessageBubble';

export type PwaChatRenderItem =
  | { type: 'message'; message: ChatMessageView }
  | {
      type: 'album';
      id: string;
      sender: string;
      images: ChatAttachmentView[];
      messageIds: string[];
      timestamp: string;
      /** Estado combinado de los mensajes del álbum. null si ninguno trae estado. */
      deliveryStatus: string | null;
    };

const DELIVERY_RANK = ['failed', 'pending', 'sent', 'delivered', 'read'] as const;

/** El peor estado gana: un fallo o un envío a medias no se muestra como leído. */
export function combineDeliveryStatuses(
  statuses: Array<string | null | undefined>
): string | null {
  const known = statuses.filter((status): status is string =>
    Boolean(status && (DELIVERY_RANK as readonly string[]).includes(status))
  );
  if (known.length === 0) return null;
  for (const status of DELIVERY_RANK) {
    if (known.includes(status)) return status;
  }
  return null;
}

const EMPTY_CONTENT = new Set(['', '[Archivos adjuntos]', '[Audio]', '📎']);

function isImageAttachment(a: ChatAttachmentView): boolean {
  if (a.kind === 'image') return true;
  if (a.kind === 'video' || a.kind === 'audio' || a.kind === 'document') return false;
  return (a.mimeType || '').toLowerCase().startsWith('image/');
}

function hasNonImageAttachment(msg: ChatMessageView): boolean {
  return (msg.attachments ?? []).some((a) => !isImageAttachment(a));
}

export function imageAttachmentsOf(msg: ChatMessageView): ChatAttachmentView[] {
  return (msg.attachments ?? []).filter(isImageAttachment);
}

function usefulContent(msg: ChatMessageView): boolean {
  const t = (msg.content ?? '').trim();
  if (!t) return false;
  if (EMPTY_CONTENT.has(t)) return false;
  return true;
}

/** Message is only images (no useful text, no other media). */
export function isImageOnlyMessage(msg: ChatMessageView): boolean {
  if (msg.isWhisper) return false;
  const images = imageAttachmentsOf(msg);
  if (images.length === 0) return false;
  if (hasNonImageAttachment(msg)) return false;
  if (usefulContent(msg)) return false;
  return true;
}

/**
 * Collapse consecutive same-sender image-only messages into album items.
 * Labeled cabin messages (text + photos) stay as bubbles and break albums.
 */
export function groupConsecutiveImageMessages(messages: ChatMessageView[]): PwaChatRenderItem[] {
  const out: PwaChatRenderItem[] = [];
  let i = 0;
  while (i < messages.length) {
    const msg = messages[i]!;
    if (!isImageOnlyMessage(msg)) {
      out.push({ type: 'message', message: msg });
      i += 1;
      continue;
    }

    const sender = msg.sender;
    const images: ChatAttachmentView[] = [];
    const messageIds: string[] = [];
    const grouped: ChatMessageView[] = [];
    while (
      i < messages.length &&
      messages[i]!.sender === sender &&
      isImageOnlyMessage(messages[i]!)
    ) {
      const cur = messages[i]!;
      images.push(...imageAttachmentsOf(cur));
      messageIds.push(cur.id);
      grouped.push(cur);
      i += 1;
    }
    const last = grouped[grouped.length - 1] ?? msg;
    out.push({
      type: 'album',
      id: `album-${messageIds[0]}-${messageIds.length}`,
      sender,
      images,
      messageIds,
      timestamp: last.timestamp,
      deliveryStatus: combineDeliveryStatuses(grouped.map((row) => row.deliveryStatus)),
    });
  }
  return out;
}
